import { useContext, useEffect, useState } from "react";
import { CustomFormComponent } from "../shared/CustomFormComponent";
import { useForm } from "react-hook-form";
import { REVALIDATE_FORM_PROPS, infoCreateLabelsFields, TRANSLATION_LABELTYPE_OPTIONS } from "../../constants/index";
import { TranslationsSchema } from "../../validations/schemas";
import { AppContext } from "../../context";
import { useVersions } from "../../hooks/useVersions";
import { useWidgets } from "../../hooks/useWidgets";
import { useTranslations } from "../../hooks/useTranslations";
import { useConsoleTranslation } from "../../hooks/useConsoleTranslations";

interface CreateLabelsFormProps {
  functionality: string;
  actionsToExecute: Function;
  isAdminFunctionality: boolean;
  customFunctionalitiesList?: {}[];
}

let formBlankValues: {
  key: string;
  description: string;
  envalue: string;
  esvalue: string;
  type: string;
  releaseversion: string;
  widgets?: [],
  functionality?: [],
} = {
  key: "",
  description: "",
  envalue: "",
  esvalue: "",
  type: "",
  releaseversion: "",
};

export const CreateLabelsForm: React.FC<CreateLabelsFormProps> = ({
  functionality='Translations',
  actionsToExecute,
  isAdminFunctionality,
  customFunctionalitiesList,
}) => {

  const {
    t,
    appState: { user, selectedApp },
  } = useContext(AppContext);
  formBlankValues = isAdminFunctionality ? { ...formBlankValues, functionality: t(`ConsoleTranslations.${functionality}FunctionalityLabel`) } : { ...formBlankValues, widgets: [] };
  const { data: versionsData } = useVersions("DATA");
  const { data: widgets } = useWidgets("DATAWITHOUTAPPID");
  const { translationAction } = useTranslations("ACTIONS");
  const { translationAction: consoleTranslationAction } = useConsoleTranslation("ACTIONS");
  
  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: formBlankValues,
  });

  const {
    getValues,
    formState: { isValid },
  } = form;

  const [formKeysOptions, setFormKeysOptions] = useState<{
    type: {}[],
    releaseversion: {}[],
    widgets?: {}[],
  }>({
    type: TRANSLATION_LABELTYPE_OPTIONS(t).sort(
      (a, b) => a.label.localeCompare(b.label)
    ),
    releaseversion: [],
  });

  useEffect(() => {
    if (!!customFunctionalitiesList && customFunctionalitiesList?.length > 0) {
      setFormKeysOptions((prev)=>({
        ...prev,
        widgets: customFunctionalitiesList,
      }));
    }
    else {
      (!!selectedApp?.appid && !!widgets && widgets?.length > 0)
        && setFormKeysOptions((prev)=>({
          ...prev,
          widgets: widgets.map((widget) => ({
            label: widget?.widgetname,
            value: widget?.widgetname,
          }))
        }));
    };

    (formKeysOptions.releaseversion?.length === 0)
      && setFormKeysOptions((prev)=>({
        ...prev,
        releaseversion: !!versionsData ? versionsData
          ?.map((version: any) => ({
            label: version.name,
            value: version.name,
          }))
          .sort((a: any, b: any) =>
            b.label.localeCompare(a.label)
          ) : [],
      }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[formKeysOptions.releaseversion?.length, selectedApp?.appid, versionsData, widgets]);

  const onAction = async ()=>{
    const formValues = getValues();
    let actionResult;
    if ( isAdminFunctionality ) {
      actionResult = await consoleTranslationAction("CREATEALL",
        {
          key: formValues.key,
          type: formValues.type,
          functionality: [functionality],
          translations: [
            {
              value: formValues.esvalue,
              languageisocode: "es",
            },
            {
              value: formValues.envalue,
              languageisocode: "en",
            },
          ],
          createdby: user?.name,
        },
      );
    } else {
        actionResult = await translationAction("CREATE", [
          {
            label: {
              key: formValues.key,
              description: formValues.description,
              type: formValues.type,
              widgets: formValues?.widgets,
              releaseversion: formValues?.releaseversion,
              createdby: user?.name,
            },
            translations: [
              {
                value: formValues.esvalue,
                languageisocode: "es",
              },
              {
                value: formValues.envalue,
                languageisocode: "en",
              },
            ],
          },
        ]);
    };
    if (actionResult === "SUCCESS") {
        // @ts-ignore
        const firstWidget = (formValues?.widgets && formValues.widgets.length > 0)? formValues.widgets[0] : "";
        const key = formValues?.key ?? "";
        actionsToExecute(key, firstWidget);
    }
};

  useEffect(() => {
    const acceptButton = document?.getElementsByClassName('p-confirm-dialog-accept')?.item(0);
    !isValid ? acceptButton!.classList.add('p-disabled') : acceptButton!.classList.remove('p-disabled');
    // @ts-ignore
    acceptButton!.onclick = isValid ? onAction : ()=>{};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isValid]);

  return (
    <div className="create-labels-form-box">
      <CustomFormComponent
          functionality="Translations"
          fields={
            isAdminFunctionality
              ? [
                {
                  key: "functionality",
                  type: "text",
                  col: 1
                },
                ...infoCreateLabelsFields
              ]
              : ((!!formKeysOptions?.widgets && formKeysOptions.widgets.length > 0)
                ? [
                  {
                    key: "widgets",
                    type: "multiselect",
                    col: 1
                  },
                  ...infoCreateLabelsFields
                ]
                : infoCreateLabelsFields)
          }
          readOnlyObject={{ [isAdminFunctionality ? 'functionality' : 'widgets']: true }}
          form={form}
          dropdownOptions={formKeysOptions}
          schemaOfValidation={isAdminFunctionality
            ? (t) => ({
              ...TranslationsSchema(t),
              functionality: {
                required: {
                  value: true,
                  message: t("GlobalConfigurations.requiredErrorMessage"),
                },
              },
            })
            : TranslationsSchema}
          REVALIDATE_FORM_PROPS={REVALIDATE_FORM_PROPS}
      />
    </div>
  )
}